import React, { useState, useEffect, Fragment } from "react";
import { ModalX } from "../../../components";
import { Button, Tab, TabPanel, Tabs, TabsBody, TabsHeader, Spinner } from "@material-tailwind/react";
import { BriefcaseIcon, BuildingIcon, FileTextIcon, ListIcon, UserIcon } from "lucide-react";
import { AddNewLead, GetLeadById, UpdateLead, DeleteLead } from "../../../services/backend";
import { Notify, NotifyError } from "../../../utils/functions";
import {
  BUSINESS_LEADS,
  DEAL_STAGES,
  LEVELS,
  STATIC_USER_ROLES,
} from "../../../utils/enums";

// Importing sub-components
import BasicInfoTab from "./basicInfoTab";
import CompanyTab from "./companyTab";
import QualificationTab from "./qualificationTab";
import OpportunityTab from "./opertunityTab";
import GDPRTab from "./gdprTab";
import LeadSummary from "./summaryTab";
import DesiredSeatsTab from "./desiredSeatsTab";
import DesiredOfficesTab from "./desiredOfficesTab";
import { useAddUser, useGetUsers } from "../../../custom-hooks/use-users";
import { useConfirm } from "../../../custom-hooks/confirm-dialog";
import { useNavigate } from "react-router-dom";
import LeadReadOnlyView from "./leadReadOnlyView";
import { URLS } from "../../../router-x/routes";



// Initial form data
const INITIAL_FORM_DATA = {
  // Core Information
  leadSource: '',
  leadStatus: DEAL_STAGES.NEW,

  // Contact Information
  fullName: '', // removed first and last name
  jobTitle: '',
  emailAddress: '',
  secondaryEmail: '',
  phoneNumber: '',
  countryCode: '',
  mobileNumber: '',
  linkedinProfile: '',
  otherSocialMedia: {},
  customerId: null,
  cnic: '',
  connectionDate: '',

  // Company Information
  companyName: '',
  companyWebsite: '',
  industry: '',
  companySize: '',
  annualRevenue: '',
  companyAddress: {
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
  },
  companyPhone: '',

  // Qualification Data
  budgetRange: '',
  decisionTimeline: '',
  isDecisionMaker: false,
  decisionProcess: '',
  painPoints: [],
  requirements: [],
  solutionInterest: [],
  competitorConsideration: [],

  // Engagement History
  engagementHistory: [],
  firstContactDate: '',
  lastContactDate: '',

  // Lead Scoring
  score: 0,
  engagementLevel: LEVELS.MEDIUM,
  purchaseIntent: '',
  conversionProbability: 0,

  // Assignment Information
  desiredSeatsCount: 0,
  desiredSeats: [],
  desiredOfficesCount: 0,
  desiredOffices: [],
  assignedSalesRep: null,
  salesTeam: '',
  assignmentDate: '',
  territory: '',

  // Follow-up Planning
  nextAction: '',
  actionDueDate: '',
  followUpNotes: '',
  followUpPriority: LEVELS.MEDIUM,

  // Opportunity Details
  opportunityDetails: {
    opportunityId: '',
    estimatedDealSize: 0,
    productsInterested: [],
    proposalStatus: '',
    negotiationStage: '',
    projectedCloseDate: ''
  },

  // Additional Information
  tags: [],
  customFields: {},
  notes: '',
  attachments: [],

  // GDPR/Privacy Compliance
  communicationConsent: false,
  consentDate: '',
  communicationPreferences: {
    email: false,
    phone: false,
    sms: false,
    directMail: false,
    whatsapp: false
  },
  dataRetentionDate: '',
  dataUsagePermissions: []
};

// Modal tabs
const modalTabs = [
  {
    label: 'Basic Info',
    value: 'basic',
    icon: <UserIcon size={12} />,
  },
  {
    label: 'Company',
    value: 'company',
    icon: <BuildingIcon size={12} />,
  },
  {
    label: 'Seats',
    value: 'seats',
    icon: <BuildingIcon size={12} />,
  },
  {
    label: 'Offices',
    value: 'offices',
    icon: <BuildingIcon size={12} />,
  },
  {
    label: 'Qualification',
    value: 'qualification',
    icon: <ListIcon size={12} />,
  },
  {
    label: 'Opportunity',
    value: 'opportunity',
    icon: <BriefcaseIcon size={12} />,
  },
  {
    label: 'GDPR',
    value: 'gdpr',
    icon: <FileTextIcon size={12} />,
  },

];

const LeadFormModal = ({ isOpen, onClose, leadId, isUpdating, onSuccess, readOnly }) => {

  // State
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  // const [salesReps, setSalesReps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // Arrays management
  const [newPainPoint, setNewPainPoint] = useState('');
  const [newRequirement, setNewRequirement] = useState('');
  const [newSolutionInterest, setNewSolutionInterest] = useState('');
  const [newCompetitor, setNewCompetitor] = useState('');
  const [newTag, setNewTag] = useState('');
  const [newProduct, setNewProduct] = useState('');

  const { confirm, ConfirmationDialog } = useConfirm();
  const navigate = useNavigate();
  const addCustomerMutation = useAddUser();


  const {
    data: salesReps,
    // isLoading: isUsersLoading, 
    // error: usersError 
  } = useGetUsers({
    role: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.EMPLOYEE],
    _limit: 1000
  });


  // Fetch lead data when the modal opens for editing
  useEffect(() => {
    const fetchLead = async () => {
      if (isUpdating && leadId && isOpen) {
        setLoading(true);
        try {
          const response = await GetLeadById(leadId);
          const lead = response.data.lead;
          setFormData({
            ...lead,
            assignedSalesRep: lead.assignedSalesRep?._id || null,
          });
        } catch (error) {
          console.error('Error fetching lead details', error);
          NotifyError('Failed to fetch lead details', error);
        } finally {
          setLoading(false);
        }
      }
    };

    if (isOpen) {
      if (isUpdating && leadId) {
        fetchLead();
      } else {
        // Reset form data for new lead
        setFormData({
          ...INITIAL_FORM_DATA,
          leadStatus: DEAL_STAGES.NEW,
          engagementLevel: LEVELS.MEDIUM,
          followUpPriority: LEVELS.MEDIUM,
          opportunityDetails: {
            ...INITIAL_FORM_DATA.opportunityDetails,
            proposalStatus: BUSINESS_LEADS.NEW,
          }
        });
      }

      // fetchSalesReps();
      setActiveTab('basic');
    }
  }, [isOpen, isUpdating, leadId]);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle nested object input change
  const handleNestedInputChange = (parentKey, childKey, value) => {
    setFormData((prev) => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: value
      }
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckBoxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleNestedCheckBoxChange = (parentKey, childKey, checked) => {
    setFormData((prev) => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: checked
      }
    }));
  };

  // Array field handlers
  const handleAddPainPoint = () => {
    if (newPainPoint.trim()) {
      setFormData(prev => ({
        ...prev,
        painPoints: [...(prev.painPoints || []), newPainPoint.trim()]
      }));
      setNewPainPoint('');
    }
  };

  const handleRemovePainPoint = (index) => {
    setFormData(prev => ({
      ...prev,
      painPoints: (prev.painPoints || []).filter((_, i) => i !== index)
    }));
  };

  const handleAddRequirement = () => {
    if (newRequirement.trim()) {
      setFormData(prev => ({
        ...prev,
        requirements: [...(prev.requirements || []), newRequirement.trim()]
      }));
      setNewRequirement('');
    }
  };

  const handleRemoveRequirement = (index) => {
    setFormData(prev => ({
      ...prev,
      requirements: (prev.requirements || []).filter((_, i) => i !== index)
    }));
  };

  const handleAddSolutionInterest = () => {
    if (newSolutionInterest.trim()) {
      setFormData(prev => ({
        ...prev,
        solutionInterest: [...(prev.solutionInterest || []), newSolutionInterest.trim()]
      }));
      setNewSolutionInterest('');
    }
  };

  const handleRemoveSolutionInterest = (index) => {
    setFormData(prev => ({
      ...prev,
      solutionInterest: (prev.solutionInterest || []).filter((_, i) => i !== index)
    }));
  };

  const handleAddCompetitor = () => {
    if (newCompetitor.trim()) {
      setFormData(prev => ({
        ...prev,
        competitorConsideration: [...(prev.competitorConsideration || []), newCompetitor.trim()]
      }));
      setNewCompetitor('');
    }
  };

  const handleRemoveCompetitor = (index) => {
    setFormData(prev => ({
      ...prev,
      competitorConsideration: (prev.competitorConsideration || []).filter((_, i) => i !== index)
    }));
  };

  const handleAddProduct = () => {
    if (newProduct.trim()) {
      setFormData(prev => ({
        ...prev,
        opportunityDetails: {
          ...prev.opportunityDetails,
          productsInterested: [...(prev.opportunityDetails?.productsInterested || []), newProduct.trim()]
        }
      }));
      setNewProduct('');
    }
  };

  const handleRemoveProduct = (index) => {
    setFormData(prev => ({
      ...prev,
      opportunityDetails: {
        ...prev.opportunityDetails,
        productsInterested: (prev.opportunityDetails?.productsInterested || []).filter((_, i) => i !== index)
      }
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim()) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (index) => {
    setFormData(prev => ({
      ...prev,
      tags: (prev.tags || []).filter((_, i) => i !== index)
    }));
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData };
    try {
      if (isUpdating) {
        if (formData.leadStatus === DEAL_STAGES.WON) {
          const isConfirmed = await confirm({
            title: 'Convert Lead to Customer Confirmation',
            message: <div className="text-sm">
              You have selected lead status as <span className="font-bold text-green-500">Won</span>.
              This action will convert the lead to a customer.
              <div className="mt-2">
                Are you sure you want to proceed?
              </div>
            </div>,
            confirmColor: 'green',
            confirmText: 'Yes Proceed',
            cancelText: 'No Just Update Lead',
          });
          if (isConfirmed) {
            setLoading(true);
            // ** Add new customer logic
            const newUser = {
              email: payload.emailAddress,
              name: payload.fullName,
              cnic: payload.cnic,
              phone: payload.countryCode + payload.phoneNumber
            };
            const response = await addCustomerMutation.mutateAsync(newUser);
            const customerId = response.data.user._id;
            await UpdateLead(leadId, { ...payload, customerId });
            Notify('Lead updated successfully', 'success');

            const consentForContract = await confirm({
              title: 'Contract for Customer Confirmation',
              message: 'Lets Proceed to create a contract for this customer',
              confirmColor: 'green',
              confirmText: 'Confirm',
              cancelText: 'Cancel',
            });
            if (consentForContract) {
              navigate(`${URLS.ADMIN.CUSTOMER}/${customerId}`);
            }
            onSuccess();
            onClose();
          }
          else {
            await UpdateLead(leadId, { ...payload, customerId: null });
            Notify('Lead updated successfully', 'success');
            onSuccess();
            onClose();
          }
        } else {
          setLoading(true);
          await UpdateLead(leadId, payload);
          Notify('Lead updated successfully', 'success');
          onSuccess();
          onClose();
        }
      } else {
        if (formData.leadStatus === DEAL_STAGES.WON) {
          const isConfirmed = await confirm({
            title: 'Convert Lead to Customer Confirmation',
            message: <div className="text-sm">
              You have selected lead status as <span className="font-bold text-green-500">Won</span>.
              This action will convert the lead to a customer.
              <div className="mt-2">
                Are you sure you want to proceed?
              </div>
            </div>,
            confirmColor: 'green',
            confirmText: 'Yes Proceed',
            cancelText: 'No Just Update Lead',
          });
          if (isConfirmed) {
            setLoading(true);
            // ** Add new customer logic
            const newUser = {
              email: payload.emailAddress,
              name: payload.fullName,
              cnic: payload.cnic,
              phone: payload.countryCode + payload.phoneNumber
            };
            const response = await addCustomerMutation.mutateAsync(newUser);
            const customerId = response.data.user._id;
            await AddNewLead({ ...payload, customerId });
            Notify('Lead updated successfully', 'success');

            const consentForContract = await confirm({
              title: 'Contract for Customer Confirmation',
              message: 'Lets Proceed to create a contract for this customer',
              confirmColor: 'green',
              confirmText: 'Confirm',
              cancelText: 'Cancel',
            });
            if (consentForContract) {
              navigate(`${URLS.ADMIN.CUSTOMER}/${customerId}`);
            }
            onSuccess();
            onClose();
          }
          else {
            await AddNewLead({ ...payload, customerId: null });
            Notify('Lead updated successfully', 'success');
            onSuccess();
            onClose();
          }
        }
        else {
          setLoading(true);
          await AddNewLead(payload);
          Notify('Lead added successfully', 'success');
          onSuccess();
          onClose();
        }
      }

    } catch (error) {
      console.error('Error saving lead', error);
      NotifyError('Failed to save lead', error);
    } finally {
      setLoading(false);
    }
  };

  // Delete lead
  const handleDeleteLead = async () => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      try {
        await DeleteLead(leadId);
        Notify('Lead deleted successfully', 'success');
        onSuccess();
        onClose();
      } catch (error) {
        console.error('Error deleting lead', error);
        NotifyError('Failed to delete lead', error);
      }
    }
  };

  // Gather all handlers and state for child components
  const handlers = {
    handleInputChange,
    handleNestedInputChange,
    handleSelectChange,
    handleCheckBoxChange,
    handleNestedCheckBoxChange,
    handleAddPainPoint,
    handleRemovePainPoint,
    handleAddRequirement,
    handleRemoveRequirement,
    handleAddSolutionInterest,
    handleRemoveSolutionInterest,
    handleAddCompetitor,
    handleRemoveCompetitor,
    handleAddProduct,
    handleRemoveProduct,
    handleAddTag,
    handleRemoveTag
  };

  const arrayState = {
    newPainPoint,
    setNewPainPoint,
    newRequirement,
    setNewRequirement,
    newSolutionInterest,
    setNewSolutionInterest,
    newCompetitor,
    setNewCompetitor,
    newTag,
    setNewTag,
    newProduct,
    setNewProduct
  };

  if (readOnly) {
    return (
      <ModalX
        isOpen={isOpen}
        onClose={onClose}
        title="Lead Summary"
        maxWidth="xxxl"
      >
        {
          loading ? (
            <div className="flex items-center justify-center min-h-[700px]">
              <Spinner />
            </div>
          ) : (
            <LeadReadOnlyView lead={formData} />
          )
        }
      </ModalX>
    )
  }

  return (
    <ModalX
      isOpen={isOpen}
      onClose={onClose}
      title={readOnly ? 'Lead Summary' : (isUpdating ? "Update Lead" : "Add New Lead")}
      maxWidth={readOnly ? "xxl" : "xxxl"}
    >
      {
        loading ? (
          <div className="flex items-center justify-center min-h-[700px]">
            <Spinner />
          </div>
        )
          :
          <Fragment>
            <div className="flex gap-5 flex-col md:flex-row min-h-[700px]">
              {
                !readOnly &&
                <div className="w-full md:w-8/12">
                  <form onSubmit={handleFormSubmit}>
                    <div className={`flex flex-col justify-between min-h-[700px]`}>
                      <div className="space-y-2">
                        <div className="pb-3">
                          <Tabs value={activeTab} onChange={(value) => setActiveTab(value)}>
                            <TabsHeader className="rounded-full" indicatorProps={{ className: 'rounded-full' }}>
                              {modalTabs.map((tab, index) => (
                                <Tab key={index} className="rounded-full font-thin text-xs" value={tab.value}>
                                  <span className="hidden md:block">{tab.label}</span>
                                  <span className="block md:hidden">{tab.icon}</span>
                                </Tab>
                              ))}
                            </TabsHeader>
                            <TabsBody className="min-h-[500px]">
                              {/* Basic Info Tab */}
                              <TabPanel value="basic">
                                <BasicInfoTab
                                  formData={formData}
                                  handlers={handlers}
                                  salesReps={salesReps?.users}
                                />
                              </TabPanel>

                              {/* Company Tab */}
                              <TabPanel value="company">
                                <CompanyTab
                                  formData={formData}
                                  handlers={handlers}
                                />
                              </TabPanel>

                              {/* Seats Tab */}
                              <TabPanel value="seats">
                                <DesiredSeatsTab
                                  formData={formData}
                                  handlers={handlers}
                                />
                              </TabPanel>

                              {/* Offices Tab */}
                              <TabPanel value="offices">
                                <DesiredOfficesTab
                                  formData={formData}
                                  handlers={handlers}
                                />
                              </TabPanel>

                              {/* Qualification Tab */}
                              <TabPanel value="qualification">
                                <QualificationTab
                                  formData={formData}
                                  handlers={handlers}
                                  arrayState={arrayState}
                                />
                              </TabPanel>

                              {/* Opportunity Tab */}
                              <TabPanel value="opportunity">
                                <OpportunityTab
                                  formData={formData}
                                  handlers={handlers}
                                  arrayState={arrayState}
                                />
                              </TabPanel>

                              {/* GDPR Tab */}
                              <TabPanel value="gdpr">
                                <GDPRTab
                                  formData={formData}
                                  handlers={handlers}
                                  arrayState={arrayState}
                                />
                              </TabPanel>
                            </TabsBody>
                          </Tabs>
                        </div>
                      </div>
                      <div className={``}>
                        <hr />
                        <div className="mt-4 flex justify-end space-x-2">
                          <Button
                            onClick={onClose}
                            className="rounded-full"
                            variant="text"
                          >
                            Cancel
                          </Button>
                          {isUpdating && (
                            <Button
                              onClick={handleDeleteLead}
                              className="rounded-full hidden"
                              color="red"
                              type="button"
                            >
                              Delete Lead
                            </Button>
                          )}
                          <Button
                            type="submit"
                            className="rounded-full"
                            disabled={loading}
                          >
                            {isUpdating ? "Update Lead" : "Add Lead"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              }
              {/* Summary/Preview Panel */}
              <div className={`${readOnly ? 'w-full' : 'w-full md:w-4/12'} hidden md:block border rounded-3xl p-4`}>
                <LeadSummary formData={formData} />
              </div>
            </div>
            {/* <CustomerFormModal
              isOpen={customerFormModalOpen}
              onClose={() => setCustomerFormModalOpen(false)}
              defaultData={{
                name: formData.firstName + ' ' + formData.lastName,
                email: formData.emailAddress,
                phone: formData.phoneNumber,
                cnic: '',
                role: STATIC_USER_ROLES.USER,
              }}
            /> */}

          </Fragment>
      }
      <ConfirmationDialog />
    </ModalX>
  );
};

export default LeadFormModal;